import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { BreadcrumbLabelContext, BreadcrumbMemberContext } from './nonAdminBreadcrumbModel';

type BreadcrumbOverrides = {
  labels?: BreadcrumbLabelContext;
  memberContext?: BreadcrumbMemberContext | null;
};

type BreadcrumbOverridesState = BreadcrumbOverrides & {
  sourcePathname: string;
};

type BreadcrumbOverridesContextValue = {
  clearBreadcrumbOverrides: () => void;
  currentOverrides: BreadcrumbOverrides | null;
  setBreadcrumbOverrides: (next: BreadcrumbOverrides) => void;
};

const BreadcrumbOverridesContext = createContext<BreadcrumbOverridesContextValue | null>(null);

export const BreadcrumbOverridesProvider: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const [state, setState] = useState<BreadcrumbOverridesState | null>(null);

  const setBreadcrumbOverrides = useCallback(
    (next: BreadcrumbOverrides) => {
      setState((previous) => {
        const base =
          previous && previous.sourcePathname === location.pathname
            ? previous
            : { sourcePathname: location.pathname };

        return {
          sourcePathname: location.pathname,
          memberContext: next.memberContext !== undefined ? next.memberContext : base.memberContext,
          labels: {
            ...(base.labels ?? {}),
            ...(next.labels ?? {}),
          },
        };
      });
    },
    [location.pathname]
  );

  const clearBreadcrumbOverrides = useCallback(() => {
    setState(null);
  }, []);

  const currentOverrides = useMemo(() => {
    if (!state || state.sourcePathname !== location.pathname) {
      return null;
    }
    return {
      labels: state.labels,
      memberContext: state.memberContext,
    };
  }, [location.pathname, state]);

  const value = useMemo(
    () => ({
      clearBreadcrumbOverrides,
      currentOverrides,
      setBreadcrumbOverrides,
    }),
    [clearBreadcrumbOverrides, currentOverrides, setBreadcrumbOverrides]
  );

  return (
    <BreadcrumbOverridesContext.Provider value={value}>
      {children}
    </BreadcrumbOverridesContext.Provider>
  );
};

export const useBreadcrumbOverrides = (): BreadcrumbOverridesContextValue => {
  const context = useContext(BreadcrumbOverridesContext);
  if (!context) {
    throw new Error('useBreadcrumbOverrides must be used within BreadcrumbOverridesProvider');
  }
  return context;
};
